import { type z } from 'zod';

import { mainLogger } from '../logger';

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint, @typescript-eslint/no-explicit-any -- musts be any or type is not inherited
type MaybePromise<T extends any> = T | Promise<T>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- musts be any or type is not inherited
type CachableValueSchema = z.AnyZodObject | z.ZodArray<any> | z.ZodNullable<CachableValueSchema>;

const logger = mainLogger.createChild('withSessionStorageCache');

class Storage<TSchema extends CachableValueSchema, TValue = z.output<CachableValueSchema>> {
  readonly schema: TSchema;

  constructor(schema: TSchema) {
    this.schema = schema;
  }

  set(key: string, value: TValue) {
    sessionStorage.setItem(key, JSON.stringify(value));
  }

  get(key: string): TValue | null {
    const valueFromStorage = sessionStorage.getItem(key);

    if (!valueFromStorage) {
      return null;
    }

    const validated = this.schema.safeParse(JSON.parse(valueFromStorage));

    if (!validated.success) {
      logger.error(new Error(`Failed to get the value from session storage by key "${key}" as its value is invalid`), {
        issues: validated.error.format(),
      });

      // Clear invalid value
      this.clear(key);

      return null;
    }

    return validated.data as TValue;
  }

  clear(key: string) {
    sessionStorage.removeItem(key);
  }
}

type CachedFunctionOptions = { pauseCaching?: boolean };

export const withSessionStorageCache = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- musts be any or type is not inherited
  TFunction extends (...parameters: any[]) => MaybePromise<TReturnValue>,
  TSchema extends CachableValueSchema,
  TReturnValue extends z.output<TSchema>,
>(
  options: {
    /** Cache key */
    key: string | ((...parameters: [...Parameters<TFunction>, opt?: CachedFunctionOptions]) => string);

    /** Validation schema that is used for validating saved data in session storage */
    schema: TSchema;
  },
  /** Function to be cached */
  functionToBeCached: TFunction,
) => {
  const { key: keyOrKeyAsFunction, schema } = options;
  const storage = new Storage(schema);

  async function cached(...parameters: [...Parameters<TFunction>, opt?: CachedFunctionOptions]): Promise<TReturnValue> {
    const key = typeof keyOrKeyAsFunction === 'function' ? keyOrKeyAsFunction(...parameters) : keyOrKeyAsFunction;

    const lastArgument = parameters.at(-1);
    const isCachingPaused =
      lastArgument !== undefined && 'pauseCaching' in lastArgument && lastArgument.pauseCaching === true;
    if (!isCachingPaused) {
      const cachedValue = storage.get(key);

      if (cachedValue !== undefined && cachedValue !== null) {
        return cachedValue as TReturnValue;
      }
    }

    const uncachedValue = (await Promise.resolve(functionToBeCached(...parameters))) as TReturnValue;

    if (!isCachingPaused && uncachedValue !== null && uncachedValue !== undefined) {
      storage.set(key, uncachedValue);
    }

    return uncachedValue;
  }

  return cached;
};
